import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AccountType, IntakeFormType, IntakeEvictionType } from '@ee/common/enums';
import { CaseSettings, Eviction, IntakeEviction, IntakeForm, LayoutRule, Organization, User, Workflow } from '@ee/common/models';
import { IntakeEvictionService, IntakeFormService } from '@ee/common/services';
import { FileStillUploadingError, FileUploadFailedError } from '@ee/common/custom-field';
import { ToCreateStepCompletionPayload } from '@ee/common/forms';
import { IntakeEvictionFormService } from '../services/intake-eviction-form.service';
import {
  FieldDefinitionView,
  LayoutPageView,
  SubmissionParts,
  UNIT_TYPE_OPTION_KEY,
  buildIntakeForm,
  toDefinitions,
  toPages,
  toSubmissionParts
} from '@ee/common/intake-layout';

/**
 * The v2 client wizard, wired to the same submission as v1.
 *
 * A separate component rather than a rewrite of `CreateEvictionComponent`, because the invariant for
 * this whole effort is that v1 keeps working *and stays editable* until the last firm is migrated. The
 * two are chosen between by `EvictionIntakeShellComponent`, so all three hosts — the public link, the
 * portal wizard and the PM send-to-attorney slide-in — get the switch without knowing about it.
 *
 * What it does not reimplement: the submission. The built `IntakeEviction` goes through the same
 * service call, and `submitHandler` is honoured, so the handoff path keeps working.
 */
@Component({
  selector: 'ee-eviction-wizard-v2',
  // Declared in `EvictionIntakeModule`, not standalone: it renders v1's own components, and those are
  // module-declared. A standalone component cannot import them directly.
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loading()) {
      <p class="wizard-v2__loading">Loading the form…</p>
    } @else if (submitted()) {
      <!--
        The firm's "What happens next", once the filing has gone. Not a step: shown before submit it
        reads as one more thing to fill in, and it describes what follows a submission that has not
        happened yet.
      -->
      @for (page of postSubmitPages(); track page.id) {
        <div class="wizard-v2__post-submit">
          <h1 class="wizard-v2__post-submit-title">{{ page.name }}</h1>
          @for (section of page.sections; track section.key) {
            <section class="ee__intake-card">
              @if (section.title) {
                <header class="ee__intake-card-header">
                  <h2 class="ee__intake-card-title">{{ section.title }}</h2>
                </header>
              }
              <ee-intake-layout-renderer [rows]="section.rows" [definitions]="definitions()"
                                         [preview]="true"></ee-intake-layout-renderer>
            </section>
          }
        </div>
      }
    } @else if (form) {
      <ee-intake-wizard [pages]="pages()" [form]="form" [definitions]="definitions()"
                        [rules_]="rules()" [unitTypes]="unitTypes()"
                        [leadingStepName]="firmStep ? 'Case setup' : undefined"
                        leadingStepNote="Who this is for, and how your firm will run it."
                        [leadingStepValid]="firmStepValid()" [submitting]="submitting()"
                        (submit)="save()">
        @if (firmStep) {
          <!--
            The firm's own step. None of it is a question for the client, and all of it decides what the
            rest of the case becomes: which client, which workflow, which attorney, and the workflow's
            own intake fields.

            Reusing v1's components rather than rebuilding them. They are driven by
            IntakeEvictionFormService, which already validates this exact group — and the client, the
            workflow and the step fields are not layout at all, so there is nothing here for the
            authored rows to describe.
          -->
          <div wizardLeadingStep class="wizard-v2__firm-step">
            <ee-workflow-form [cardStyle]="true" [preSelectedClientId]="selectedClientId" [cloneCase]="cloneCase"
                              (addClient)="addClient.emit($event)"
                              (workflowSelected)="onWorkflowSelected($event)"></ee-workflow-form>

            @if (selectedWorkflow() && formService.intakeStep) {
              <section class="ee__intake-card">
                <header class="ee__intake-card-header">
                  <h2 class="ee__intake-card-title">Intake fields</h2>
                  <!-- The workflow's name is data, so it reads as the lede rather than shouting in caps. -->
                  <p class="ee__intake-card-lede">{{ selectedWorkflow()?.name }}</p>
                </header>
                <ee-complete-workflow-step
                  [caseSettings]="caseSettings" [nextStep]="formService.intakeStep"
                  [clientId]="formService.workflowDetails?.get('client_details')?.value?.id"
                  [userSuggestions]="formService.userSuggestions"
                  [tenants]="formService.tenants"
                  [planAllowsCaseTasks]="false"
                  [parentForm]="formService.splitForm" [hideLabel]="true" [forceVertical]="true" />
              </section>
            }
          </div>
        }
      </ee-intake-wizard>
    }
  `,
  styles: [
    `
      /*
       * The step's own stacking. ee__intake-card spaces one card off the card before it, but here the
       * workflow form's cards are inside a wrapper element, so the intake-fields card has no card
       * sibling to space off — the gap has to come from the container.
       */
      .wizard-v2__firm-step {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .wizard-v2__post-submit {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .wizard-v2__post-submit-title {
        margin: 0;
      }

      .wizard-v2__loading {
        padding: 32px;
        text-align: center;
        color: var(--color-on-surface-variant);
      }

    `
  ]
})
export class EvictionWizardV2Component implements OnInit, OnDestroy {
  /** Whose form to render. Absent for a signed-in client, whose attorney is already known. */
  @Input() attorneyId?: string;

  /** True on the public link, where page one creates the account. */
  @Input() forceIntakeForm = false;

  /** Property-linked PM mode: the server synthesizes the client, so page one is not asked. */
  @Input() skipClientStep = false;

  /** The handoff path submits through its own endpoint — create intake, link, freeze the case. */
  @Input() submitHandler?: (intake: IntakeEviction) => Observable<string | undefined>;

  @Input() loggedInOrg?: Organization;

  @Input() loggedInUser?: User;

  @Input() caseSettings?: CaseSettings;

  @Input() selectedClientId?: string;

  @Input() cloneCase?: Eviction;

  @Output() evictionCreated = new EventEmitter<string | undefined>();

  @Output() addClient = new EventEmitter<boolean>();

  private readonly intakeFormService = inject(IntakeFormService);
  /** Public so the template can read the group v1's own components are bound to. */
  protected readonly formService = inject(IntakeEvictionFormService);
  private readonly intakeEvictionService = inject(IntakeEvictionService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  /** Set when this wizard initialized the firm step, so it clears only state it owns. */
  private initializedFirmStep = false;

  protected readonly loading = signal(true);
  protected readonly pages = signal<LayoutPageView[]>([]);
  protected readonly definitions = signal<Map<string, FieldDefinitionView>>(new Map());
  protected readonly rules = signal<LayoutRule[]>([]);
  protected readonly unitTypes = signal<string[]>([]);
  /** The authored "What happens next" pages, kept out of the steps and shown once the submit succeeds. */
  protected readonly postSubmitPages = signal<LayoutPageView[]>([]);
  protected readonly submitted = signal(false);
  /**
   * A submit is in flight. Set before subscribing and cleared only on error: after success the host
   * navigates away or the post-submit page replaces the wizard, and a second request would file twice.
   */
  protected readonly submitting = signal(false);

  protected form?: FormGroup;

  /**
   * Whether the firm is the one filling this in.
   *
   * An attorney creating a case on a client's behalf chooses the client, the workflow and the attorney,
   * and answers the workflow's intake fields. A client filling their own form is the client, has one
   * attorney, and has no business seeing any of it — and the public link has nobody signed in at all.
   */
  protected get firmStep(): boolean {
    return !this.forceIntakeForm && this.loggedInOrg?.type === AccountType.ATTORNEY;
  }

  protected readonly selectedWorkflow = signal<Workflow | undefined>(undefined);

  private intakeForm?: IntakeForm;

  ngOnInit(): void {
    /*
     * The firm step is driven by `IntakeEvictionFormService`, so it has to be initialised even though
     * the authored pages do not use it. Awaited before the wizard renders: `ee-workflow-form` binds
     * straight to `workflowDetails`, and rendering it against an uninitialised service is a template
     * reading controls that do not exist yet.
     */
    if (this.firmStep) {
      this.initializedFirmStep = true;
      void this.formService.initialize(
        false,
        this.attorneyId ?? this.loggedInOrg?.id ?? '',
        this.loggedInUser ?? null,
        this.loggedInOrg ?? null,
        this.cloneCase ?? null
      );
    }

    const load = this.attorneyId
      ? this.intakeFormService.findByTypeAndOrganizationId(IntakeFormType.EVICTION, this.attorneyId)
      : this.intakeFormService.findByType(IntakeFormType.EVICTION);

    load.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: form => this.build(form),
      error: () => {
        this.loading.set(false);
        this.snackBar.open('We could not load the form. Please try again shortly.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  /**
   * `IntakeEvictionFormService` is root-scoped and `initialize()` returns at once when already
   * initialized, so the next intake would reuse this one's client, workflow and grafted step answers.
   * v1 clears it the same way. The submit is deliberately not cancelled: it may already have reached
   * the server, and the host navigates away on success.
   */
  ngOnDestroy(): void {
    if (this.initializedFirmStep) {
      this.formService.clearForm();
    }
  }

  private build(form: IntakeForm): void {
    this.intakeForm = form;
    const definitions = new Map(toDefinitions(form).map(definition => [definition.key, definition]));

    /*
     * Page one is dropped for a signed-in client, and for property-linked PM mode.
     *
     * It asks for an email, a phone and a company name to open an account — questions a client who is
     * already signed in has answered, and which the server synthesizes in PM mode. Asking them again
     * is how the same firm ends up in the database twice.
     */
    const all = toPages(form);
    const pages = all.filter(page => !page.post_submit && (this.forceIntakeForm || !page.public_only));

    this.pages.set(this.skipClientStep ? pages.filter(page => !page.public_only) : pages);
    this.postSubmitPages.set(all.filter(page => page.post_submit && page.sections.length));
    this.definitions.set(definitions);
    this.rules.set(form.layout_rules ?? []);
    this.unitTypes.set(
      (form.options as unknown as Record<string, { options?: string[] }>)?.[UNIT_TYPE_OPTION_KEY]?.options ?? []
    );
    this.form = buildIntakeForm(this.pages(), definitions);
    this.loading.set(false);
  }

  protected onWorkflowSelected(workflow: Workflow): void {
    this.selectedWorkflow.set(workflow);
  }

  /**
   * The firm's step is answered when v1's whole split form is valid, as v1 checks it on save.
   *
   * Not just the workflow-selection group: `ee-complete-workflow-step` grafts the workflow's intake
   * fields onto `splitForm.step_completion_details`, and their required controls belong to this step.
   */
  protected firmStepValid(): boolean {
    return !this.firmStep || this.formService.splitForm?.valid === true;
  }

  protected save(): void {
    if (!this.form || !this.intakeForm || this.submitting()) {
      return;
    }

    // Continue is gated on the same check, but nothing after the firm step re-validates it.
    if (!this.firmStepValid()) {
      this.formService.splitForm?.markAllAsTouched();
      this.snackBar.open('Case setup is incomplete. Go back to it to finish the intake fields.', 'Dismiss', {
        duration: 5000
      });
      return;
    }

    let parts: SubmissionParts;
    try {
      parts = toSubmissionParts(this.pages(), this.form, this.definitions(), {
        includeAccount: this.forceIntakeForm && !this.skipClientStep
      });
    } catch (e) {
      // A document still uploading, or one whose upload failed: nothing is sent, and the client is told which.
      if (e instanceof FileStillUploadingError || e instanceof FileUploadFailedError) {
        this.snackBar.open(e.message, 'Dismiss', { duration: 5000 });
        return;
      }
      throw e;
    }

    /*
     * The firm's answers, where there is a firm step.
     *
     * `client_details` comes from the *selected client* rather than from page one — an attorney is
     * filing on someone's behalf, and page one is not even asked. Same for the workflow, the assigned
     * attorney and the step-completion fields: they are the firm's decisions about the case, and none
     * of them is expressible as a placement on the client's form.
     */
    const firm = this.firmStep ? (this.formService.workflowDetails?.value ?? {}) : {};
    const split = this.firmStep ? (this.formService.splitForm?.value ?? {}) : {};

    const intake = {
      type: this.attorneyId ? IntakeEvictionType.INTAKE_FORM : IntakeEvictionType.EXISTING_CLIENT,
      attorney_id: firm.attorney_id ?? this.attorneyId,
      intake_form_id: this.intakeForm.id,
      selected_workflow_id: firm.workflow_id,
      selected_attorney_user_id: firm.selected_attorney_user_id,
      // The grafted step form's raw value, so the wire conversions v1 applies are applied here too.
      step_completion_details: ToCreateStepCompletionPayload(split.step_completion_details),
      note: split.note,
      eviction_details: parts.evictionDetails,
      user_details: parts.userDetails ?? firm.user_details,
      client_details: parts.clientDetails ?? firm.client_details,
      // Staged uploads, as v1's documents step sends them; the legacy per-type map is still read by a
      // server that predates `uploads`.
      uploads: parts.uploads,
      upload_ids: Object.fromEntries(parts.uploads.map(upload => [upload.type, upload.upload_id]))
    } as unknown as IntakeEviction;

    const submit$ = this.submitHandler
      ? this.submitHandler(intake)
      : this.intakeEvictionService.saveIntakeEvictionWithUploads(intake.type, intake);

    this.submitting.set(true);
    submit$.subscribe({
      next: caseId => {
        // Only a page with something on it replaces the wizard; otherwise the host's own confirmation stands.
        this.submitted.set(this.postSubmitPages().length > 0);
        this.evictionCreated.emit(caseId);
      },
      error: (error: HttpErrorResponse) => {
        // `BadRequestAlertException` puts the descriptive message in `title` and the short entity name
        // in `detail`; the title is the one worth showing a client.
        const message = error.error?.title || 'An error occurred submitting your request. Please try again shortly.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
        this.submitting.set(false);
      }
    });
  }
}
